import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { Steps } from '@app/components/stepper/stepper.component';
import { PortalLeadService } from '@app/services/portal-lead/portal-lead.service';
import { PortalOrderService } from '@app/services/portal-order/portal-order.service';
import { PopupsService, PriceSummaryAction } from '@voyagu/portal-design-system';
import { State, StateService } from '@app/services/state/state.service';
import { ApiService } from '@app/services/api/api.service';
import {
  CardFormComponent,
  PortalSubmitOptionErrors,
} from '@app/forms/card-form/card-form.component';
import { clientBalanceOption, newCreditCardOption } from '@app/utils/constants';
import { Option } from '@app/forms/formly/formly-utils';
import { AuthService } from '@app/services/auth/auth.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalRoutes } from '@app/app-routing.module';
import { handleError } from '@app/operators/handle-error';
import { AuthorizedDataService } from '@app/services/authorized-data/authorized-data.service';
import {
  AncillaryTypeEnum,
  AncillaryUpsellNode,
  PortalCreditCardNodeInput,
  PortalFeatures,
  PortalOrderNode,
  PortalPassengerNodeInput,
  PortalTierEnum,
  PortalReferralCreditPreviewNode,
} from '@app/services/api/api.types';
import { calculateMaxBalanceAmount, mapPortalCreditCardNode, scrollTo } from '@app/utils/utils';
import { getBasicChangeFeeTotal } from '@app/utils/basic-change-fee';
import { StripeFormComponent } from '@app/forms/stripe-form/stripe-form.component';
import { ErrorService } from '@app/services/error.service';
import { isTiersFeature, getTierRetentionPrice } from '@app/pages/tiers-page/utils';
import { FormError } from '@app/pages/client-traveler-page/client-traveler-page.component';

@Component({
  selector: 'overview-and-payment-page',
  templateUrl: './overview-and-payment-page.component.html',
  styleUrls: ['./overview-and-payment-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverviewAndPaymentPageComponent implements OnInit {
  Steps = Steps;

  portalOrder$: Observable<PortalOrderNode | null>;
  portalOrder: PortalOrderNode;

  state: State;

  creditCards: Option[];
  stripeCreditCards: Option[];

  clientBalanceAmount: number;
  phone: string;

  model: PortalCreditCardNodeInput = { cvv2: '' };
  errors: PortalSubmitOptionErrors;

  formHasChanges: boolean;

  readAndAccept: boolean;
  readAndAcceptError: boolean;
  isDisruptionProtection: boolean;
  isCancelForAnyReason: boolean;

  clientSecret: string | null = null;

  withStripe: boolean;
  portalSequentiallyAncillaries: boolean;
  ancillaryUpsell: boolean;
  portalTiers: boolean;

  referralCreditPreview: PortalReferralCreditPreviewNode | null = null;
  referralCreditDiscount = 0;
  referralCreditNotAppliedReason: PortalReferralCreditPreviewNode['notAppliedReason'] = null;

  private destroyRef: DestroyRef;

  @ViewChild('confirmTemplate') confirmTemplate: TemplateRef<unknown>;
  @ViewChild(StripeFormComponent) stripeForm: StripeFormComponent;
  @ViewChild(CardFormComponent) cardForm: CardFormComponent;

  constructor(
    private authorizedDataService: AuthorizedDataService,
    private portalLeadService: PortalLeadService,
    private portalOrderService: PortalOrderService,
    private changeDetectorRef: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private popupsService: PopupsService,
    private stateService: StateService,
    private errorService: ErrorService,
    private authService: AuthService,
    private apiService: ApiService,
    private location: Location,
    private router: Router,
    destroyRef: DestroyRef,
  ) {
    this.destroyRef = destroyRef;
  }

  ngOnInit() {
    this.ancillaryUpsell = this.activatedRoute.snapshot.data['ancillaryUpsell'] as boolean;

    this.getPortalOrder();

    if (!this.ancillaryUpsell) {
      this.portalOrder$
        .pipe(
          filter((order) => Boolean(order)),
          tap(() => {
            this.state = this.stateService.getState();
            this.loadReferralCreditPreview();
          }),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
  }

  loadReferralCreditPreview() {
    if (this.ancillaryUpsell || !this.portalOrder) {
      return;
    }

    this.state = this.stateService.getState();

    const leadPortalLink =
      this.state.portalLink || this.portalLeadService.portalLead$$.getValue()?.portalLink || '';
    const optionId = this.portalOrder.portalLink;
    const passengers = this.state.passengers ?? [];

    if (!passengers.length || !leadPortalLink || !optionId) {
      this.applyReferralCreditPreview(null);
      return;
    }

    this.apiService
      .portalReferralCreditPreview({
        portalLink: leadPortalLink,
        optionId,
        passengers,
      })
      .pipe(
        take(1),
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((preview) => this.applyReferralCreditPreview(preview));
  }

  /**
   * The credit line and the total follow the preview only: it runs the same check as the payment.
   * With no answer (not requested yet, failed or null) nothing is shown, never an assumed credit.
   */
  private applyReferralCreditPreview(preview: PortalReferralCreditPreviewNode | null) {
    const resolved = preview;

    this.referralCreditPreview = resolved;
    this.referralCreditDiscount =
      resolved?.applies && resolved.amount ? Number(resolved.amount) : 0;
    this.referralCreditNotAppliedReason =
      resolved && !resolved.applies ? (resolved.notAppliedReason ?? null) : null;
    this.changeDetectorRef.markForCheck();
  }

  summaryActions: PriceSummaryAction[] = [
    { id: 'confirm', label: 'Confirm and Pay', variant: 'primary' },
    { id: 'back', label: 'Back', variant: 'secondary' },
  ];

  onSummaryAction(action: PriceSummaryAction) {
    if (action.id === 'back') {
      this.tryBack();
      return;
    }

    if (this.isFree) {
      this.submitFreeChanges();
      return;
    }

    const form = this.portalOrder.acceptStripe && this.withStripe ? this.stripeForm : this.cardForm;

    form?.submit();
  }

  get isFree() {
    const tierRetentionPrice = getTierRetentionPrice(this.portalOrder, this.state?.tier);

    return (
      this.portalOrder.isChange &&
      !this.portalOrder.soldAmount &&
      !this.state.tipsAmount &&
      !tierRetentionPrice &&
      !getBasicChangeFeeTotal(this.portalOrder)
    );
  }

  getPortalOrder() {
    this.portalOrder$ = combineLatest([
      this.portalOrderService.portalOrder$,
      this.portalLeadService.portalLead$,
    ]).pipe(
      map(([portalOrder, portalLead]) => {
        if (portalLead) {
          this.withStripe = portalLead.salesAgent?.portalFeatures?.includes(
            PortalFeatures.portal_stripe,
          );

          this.portalTiers =
            portalLead.salesAgent?.portalFeatures?.includes(PortalFeatures.portal_tiers) &&
            isTiersFeature(portalOrder);

          this.portalSequentiallyAncillaries = portalLead.salesAgent?.portalFeatures?.includes(
            PortalFeatures.portal_sequentially_ancillaries,
          );
        }

        if (portalOrder) {
          this.portalOrder = portalOrder;

          if (!this.withStripe) {
            this.withStripe = (
              portalOrder as unknown as AncillaryUpsellNode
            )?.salesAgent?.portalFeatures?.includes(PortalFeatures.portal_stripe);
          }
        }

        const phone = this.ancillaryUpsell
          ? (portalOrder as unknown as AncillaryUpsellNode)?.clientPhone
          : portalLead?.flightRequest?.client?.phone;

        if (phone) {
          this.phone = phone;
          this.model = { ...this.model, phone };
        }

        this.state = this.stateService.getState();

        this.isDisruptionProtection = !!(
          this.state.disruptionProtection || this.state.disruptionProtection === 0
        );
        this.isCancelForAnyReason = !!(
          this.state.cancelForAnyReason || this.state.cancelForAnyReason === 0
        );
        const bundle = this.state.bundle;

        if (!this.isCancelForAnyReason && bundle) {
          this.isCancelForAnyReason = bundle.ancillaries.some(
            (ancillary) => ancillary.type === AncillaryTypeEnum.CANCEL_FOR_ANY_REASON,
          );
        }

        if (!this.isDisruptionProtection && bundle) {
          this.isDisruptionProtection = bundle.ancillaries.some(
            (ancillary) => ancillary.type === AncillaryTypeEnum.DISRUPTION_PROTECTION,
          );
        }

        const creditCards =
          this.authorizedDataService
            .getAuthorizedDataValue()
            ?.creditCards?.map(mapPortalCreditCardNode) || [];

        const clientBalanceAmount =
          this.authorizedDataService.getAuthorizedDataValue()?.clientBalanceAmount || 0;

        if (creditCards.length || clientBalanceAmount) {
          creditCards.unshift(newCreditCardOption);
        }

        this.creditCards = creditCards;

        this.clientBalanceAmount = clientBalanceAmount;

        const stripeCreditCards =
          this.authorizedDataService
            .getAuthorizedDataValue()
            ?.stripeCreditCards?.map(mapPortalCreditCardNode) || [];

        if (stripeCreditCards?.length || clientBalanceAmount) {
          stripeCreditCards.unshift(newCreditCardOption);
        }

        this.stripeCreditCards = stripeCreditCards;

        Tracking.track(PortalTrackingEvents.portalCheckoutPaymentOpened);

        return portalOrder;
      }),
      // The template and ngOnInit both subscribe: run the setup and the tracking once per order.
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  valueChange() {
    this.formHasChanges = true;
  }

  tryBack() {
    if (this.formHasChanges) {
      this.popupsService.showModal(this.confirmTemplate);
      return;
    }

    this.back();
  }

  back() {
    this.location.back();

    Tracking.track(PortalTrackingEvents.portalCheckoutPaymentBackPressed);
  }

  stripeFormSubmit({
    clientBalanceAmount,
    saveCardToStripe,
    stripeCreditCardId,
  }: {
    clientBalanceAmount: number;
    saveCardToStripe: boolean;
    stripeCreditCardId: string;
  }) {
    const input = {
      portalLink: this.portalOrder.portalLink,
      passengers: this.state.passengers || [],
    };

    if (stripeCreditCardId !== clientBalanceOption.value) {
      Object.assign(input, { payByStripe: true });
    }

    const isClientBalance =
      (clientBalanceAmount && clientBalanceAmount > 0) ||
      stripeCreditCardId === clientBalanceOption.value;

    if (isClientBalance) {
      Object.assign(input, {
        clientBalanceAmount:
          stripeCreditCardId === clientBalanceOption.value
            ? calculateMaxBalanceAmount(
                this.portalOrder.maxBalanceAmount,
                this.clientBalanceAmount,
                this.state,
                this.portalOrder,
              )
            : clientBalanceAmount,
      });
    }

    if (saveCardToStripe) {
      Object.assign(input, { saveCardToStripe });
    }

    if (
      stripeCreditCardId &&
      stripeCreditCardId !== newCreditCardOption.value &&
      stripeCreditCardId !== clientBalanceOption.value
    ) {
      Object.assign(input, { stripeCreditCardId });
    }

    if (this.state.tipsAmount) {
      Object.assign(input, { tipsAmount: this.state.tipsAmount });
    }

    if (this.state.disruptionProtection || this.state.disruptionProtection === 0) {
      Object.assign(input, { disruptionProtection: true });
    }

    if (this.state.cancelForAnyReason || this.state.cancelForAnyReason === 0) {
      Object.assign(input, { cancelForAnyReason: true });
    }

    if (this.state.promocode) {
      Object.assign(input, { promocode: this.state.promocode });
    }

    if (this.authService.getUser()) {
      Object.assign(input, { anonymous: false });
    }

    const bundle = this.state.bundle;

    if (bundle) {
      Object.assign(input, {
        withBundleDiscount: true,
        disruptionProtection: true,
        cancelForAnyReason: true,
      });
    }

    if (this.state.tier) {
      Object.assign(input, { tier: this.state.tier });
    }

    return this.ancillaryUpsell
      ? this.portalAncillariesUpsell(input)
      : this.portalSubmitOption(input);
  }

  submitFreeChanges(): void {
    if (!this.readAndAccept) {
      this.readAndAcceptError = true;
      scrollTo('#read-and-accept');
      setTimeout(() => document.getElementById('read-and-accept')?.focus());
      return;
    }

    this.readAndAcceptError = false;
    this.formSubmit();
  }

  formSubmit(creditCard?: PortalCreditCardNodeInput) {
    const input = {
      portalLink: this.portalOrder.portalLink,
      passengers: this.state.passengers || [],
      creditCard: undefined as PortalCreditCardNodeInput | undefined,
    };

    if (creditCard) {
      const isClientBalance =
        (creditCard.clientBalanceAmount && creditCard.clientBalanceAmount > 0) ||
        creditCard.id === clientBalanceOption.value;

      if (isClientBalance) {
        Object.assign(input, {
          clientBalanceAmount:
            creditCard.id === clientBalanceOption.value
              ? calculateMaxBalanceAmount(
                  this.portalOrder.maxBalanceAmount,
                  this.clientBalanceAmount,
                  this.state,
                  this.portalOrder,
                )
              : creditCard.clientBalanceAmount,
        });
      }

      const isNewCreditCard = creditCard.id === newCreditCardOption.value || !creditCard.id;

      if (isNewCreditCard) {
        Object.assign(input, { creditCard: { ...creditCard } });

        if (input.creditCard) {
          delete input.creditCard['id'];
          delete input.creditCard['clientBalanceAmount'];
          delete input.creditCard['cardAmount'];
        }
      }

      const isExistingCreditCard =
        creditCard.id &&
        creditCard.id !== newCreditCardOption.value &&
        creditCard.id !== clientBalanceOption.value;

      if (isExistingCreditCard) {
        Object.assign(input, {
          creditCard: {
            id: creditCard.id,
            cvv2: creditCard.cvv2,
          },
        });
      }

      const phone = creditCard.phone;

      if (phone) {
        if (phone !== this.phone) {
          Object.assign(input, { phone });
        }

        if (input.creditCard) {
          delete input.creditCard['phone'];
        }
      }
    }

    if (!Object.keys(input.creditCard || {})?.length) {
      delete input['creditCard'];
    }

    if (this.state.promocode) {
      Object.assign(input, { promocode: this.state.promocode });
    }

    if (this.state.disruptionProtection || this.state.disruptionProtection === 0) {
      Object.assign(input, { disruptionProtection: true });
    }

    if (this.state.cancelForAnyReason || this.state.cancelForAnyReason === 0) {
      Object.assign(input, { cancelForAnyReason: true });
    }

    const bundle = this.state.bundle;

    if (bundle) {
      Object.assign(input, {
        withBundleDiscount: true,
        disruptionProtection: true,
        cancelForAnyReason: true,
      });
    }

    if (this.state.tipsAmount) {
      Object.assign(input, { tipsAmount: this.state.tipsAmount });
    }

    if (this.authService.getUser()) {
      Object.assign(input, { anonymous: false });
    }

    if (this.state.tier) {
      Object.assign(input, { tier: this.state.tier });
    }

    return this.ancillaryUpsell
      ? this.portalAncillariesUpsell(input)
      : this.portalSubmitOption(input);
  }

  portalAncillariesUpsell(variables: {
    passengers: PortalPassengerNodeInput[];
    portalLink: string;
    tipsAmount?: number;
    disruptionProtection?: boolean;
    cancelForAnyReason?: boolean;
    creditCard?: PortalCreditCardNodeInput;
    clientBalanceAmount?: number;
    anonymous?: boolean;
    payByStripe?: boolean;
    saveCardToStripe?: boolean;
    stripeCreditCardId?: string;
    promocode?: string;
    withBundleDiscount?: boolean;
    phone?: string;
    tier?: PortalTierEnum;
  }) {
    const tripPortalLink =
      this.activatedRoute.snapshot.paramMap.get(PortalRoutes.AncillaryUpsellParam) || '';

    const input = {
      tripPortalLink,
      creditCard: variables.creditCard,
      disruptionProtection: variables.disruptionProtection,
      cancelForAnyReason: variables.cancelForAnyReason,
      anonymous: variables.anonymous,
      payByStripe: variables.payByStripe,
      withBundleDiscount: variables.withBundleDiscount,
      saveCardToStripe: variables.saveCardToStripe,
      stripeCreditCardId: variables.stripeCreditCardId,
      tipsAmount: variables.tipsAmount,
      perpetual: this.state.perpetual,
      clientBalanceAmount: variables.clientBalanceAmount,
      clientPhone: variables.phone,
      tier: variables.tier,
    };

    return this.apiService
      .portalAncillariesUpsell({ input })
      .pipe(
        take(1),
        catchError((error: Error) => {
          console.log(error.message || error);
          return EMPTY;
        }),
      )
      .subscribe((response) => {
        if (input?.payByStripe && response?.ok && response?.stripeClientSecret) {
          this.clientSecret = response?.stripeClientSecret;
        } else if (!input?.payByStripe && response?.ok) {
          const path = [PortalRoutes.AncillaryUpsell, PortalRoutes.Complete];
          void this.router.navigate(path);
        } else {
          const errors = response?.errors;

          if (errors) {
            this.errors = JSON.parse(errors) as PortalSubmitOptionErrors;
            this.sendOnErrorEvent(this.errors);

            this.errorService.setError(this.errors.error);

            const path = [PortalRoutes.AncillaryUpsell, PortalRoutes.Error];
            void this.router.navigate(path);
          }
        }

        this.changeDetectorRef.detectChanges();
      });
  }

  portalSubmitOption(input: {
    passengers: PortalPassengerNodeInput[];
    portalLink: string;
    tipsAmount?: number;
    disruptionProtection?: boolean;
    cancelForAnyReason?: boolean;
    creditCard?: PortalCreditCardNodeInput;
    clientBalanceAmount?: number;
    anonymous?: boolean;
    payByStripe?: boolean;
    saveCardToStripe?: boolean;
    stripeCreditCardId?: string;
    promocode?: string;
    withBundleDiscount?: boolean;
    tier?: PortalTierEnum;
    phone?: string;
  }) {
    return this.apiService
      .portalSubmitOption({ input })
      .pipe(
        take(1),
        catchError((error: Error) => {
          console.log(error.message || error);
          return EMPTY;
        }),
      )
      .subscribe((resp) => {
        const response = resp?.data?.portalSubmitOption;

        if (input?.payByStripe && response?.ok && response?.stripeClientSecret) {
          this.clientSecret = response?.stripeClientSecret;
        } else if (!input?.payByStripe && response?.ok) {
          this.portalLeadService.getPortalLead({ portalLink: this.state.portalLink });
          this.authorizedDataService.refetchAuthorizedDataEvent();

          this.continue();
        } else {
          const errors = response?.errors;

          if (errors) {
            this.errors = JSON.parse(errors) as PortalSubmitOptionErrors;
            this.sendOnErrorEvent(this.errors);
          }

          const requestErrors = mapRequestErrors(resp?.errors as unknown as FormError[]);

          if (Object.keys(requestErrors).length) {
            this.errors = requestErrors;
            this.sendOnErrorEvent(this.errors);
          }
        }

        this.changeDetectorRef.detectChanges();
      });
  }

  stripeFormSubmitCallback() {
    this.apiService
      .portalFinishPayment({ portalLink: this.portalOrder.portalLink })
      .pipe(
        take(1),
        switchMap(() => {
          this.portalLeadService.getPortalLead({ portalLink: this.state.portalLink });
          this.authorizedDataService.refetchAuthorizedDataEvent();

          this.continue();
          return EMPTY;
        }),
        handleError(),
      )
      .subscribe();
  }

  continue() {
    const path = [
      `${this.state.portalLink}`,
      `${this.portalOrder.portalLink}`,
      PortalRoutes.Complete,
    ];

    void this.router.navigate(path, { queryParamsHandling: 'merge' });
  }

  selectReadAndAccept(readAndAccept: boolean) {
    this.readAndAccept = readAndAccept;
    this.readAndAcceptError = false;

    Tracking.track(PortalTrackingEvents.portalCheckoutPaymentTermsChecked);
  }

  applyPromocode(promocode: string) {
    const input = {
      optionPortalLink: this.portalOrder.portalLink,
      promocode,
    };

    this.apiService
      .validatePromocode({ input })
      .pipe(take(1), handleError())
      .subscribe((response) => {
        if (response?.ok) {
          this.stateService.patchState({
            promocode,
            promocodeDiscountAmount: response?.discountAmount,
          });

          this.state = this.stateService.getState();
        } else {
          const errors = response?.errors;

          if (errors) {
            this.errors = JSON.parse(errors) as PortalSubmitOptionErrors;
            this.sendOnErrorEvent(this.errors);
          }
        }

        this.changeDetectorRef.detectChanges();
      });
  }

  navigateToTravelers() {
    const path = [
      `${this.state.portalLink}`,
      `${this.portalOrder.portalLink}`,
      PortalRoutes.Travelers,
    ];

    void this.router.navigate(path, { queryParamsHandling: 'merge' });
  }

  navigateToPlans() {
    const disruptionProtection = this.portalOrder.ancillaries.some(
      (ancillary) => ancillary.type === AncillaryTypeEnum.DISRUPTION_PROTECTION,
    );

    const cancelForAnyReason = this.portalOrder.ancillaries.some(
      (ancillary) => ancillary.type === AncillaryTypeEnum.CANCEL_FOR_ANY_REASON,
    );

    let path = [
      this.stateService.getState().portalLink,
      this.portalOrder.portalLink,
      disruptionProtection || cancelForAnyReason
        ? PortalRoutes.AncillaryBundleComponent
        : PortalRoutes.Gratuity,
    ];

    if (this.portalSequentiallyAncillaries) {
      path = [
        this.stateService.getState().portalLink,
        this.portalOrder.portalLink,
        PortalRoutes.DisruptionProtection,
      ];
    }

    void this.router.navigate(path, { queryParamsHandling: 'merge' });
  }

  removePromocode() {
    this.stateService.patchState({
      promocode: undefined,
      promocodeDiscountAmount: undefined,
    });

    this.state = this.stateService.getState();
  }

  sendOnErrorEvent(errors: PortalSubmitOptionErrors): void {
    if (errors.error) {
      Tracking.track(PortalTrackingEvents.portalOverviewAndPaymentError);
    }

    if (errors.promocode) {
      Tracking.track(PortalTrackingEvents.portalOverviewAndPaymentPromocodeError);
    }

    if (errors.form) {
      Tracking.track(PortalTrackingEvents.portalOverviewAndPaymentFormError);
    }

    if (errors.client_balance_amount) {
      Tracking.track(PortalTrackingEvents.portalOverviewAndPaymentClientBalanceAmountError);
    }

    if (errors.combined_discount) {
      Tracking.track(PortalTrackingEvents.portalOverviewAndPaymentCombinedDiscountError);
    }

    if (errors.passengers) {
      errors.passengers.forEach((passenger, i) => {
        if (passenger.birthday) {
          Tracking.track(
            PortalTrackingEvents.portalOverviewAndPaymentPaxBirthdayError.replace(
              '{index}',
              i.toString(),
            ),
          );
        }
      });
    }

    if (errors.credit_card) {
      const keys = Object.keys(errors.credit_card);

      if (keys.length) {
        Tracking.track(PortalTrackingEvents.portalOverviewAndPaymentCreditCardError);
      }
    }
  }
}

// todo rewrite BE errors for the phone field
const mapRequestErrors = (errors: FormError[] = []) => {
  const mappedErrors: PortalSubmitOptionErrors = {};
  const commonMessages: string[] = [];

  errors.forEach((error) => {
    if (error.extensions?.field === 'phones') {
      mappedErrors.credit_card = { ...mappedErrors.credit_card, phone: error.message };
    } else if (error.message) {
      commonMessages.push(error.message);
    }
  });

  if (commonMessages.length) {
    mappedErrors.form = commonMessages.join(' ');
  }

  return mappedErrors;
};
